import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'

import type { YMarketItem } from '@/lib/types'

export const ABSENT_PIECE_FACT = '—'

/** The one spelling of a piece card's address. */
export function pieceCardHref(cardKey: string): string {
  return `/inventory/${encodeURIComponent(cardKey)}`
}

/** Where a press on a piece came from, so its card can send the user back. */
export interface PieceOrigin {
  href: string
  label: string | null
}

export function pieceOriginOf(state: unknown): PieceOrigin | null {
  if (typeof state !== 'object' || state === null || !('pieceOrigin' in state)) return null
  const origin = state.pieceOrigin
  if (typeof origin !== 'object' || origin === null || !('href' in origin) || !('label' in origin)) return null
  const { href, label } = origin
  if (typeof href !== 'string' || !href.startsWith('/')) return null
  if (label !== null && typeof label !== 'string') return null
  return { href, label }
}

/**
 * A piece as one surface names it.
 *
 * `productId` is the piece the surface's own row proves, and `cardKey` is the key
 * the card route takes; they travel separately because an attached piece can
 * still lack an addressable card. Reason and message are the server's.
 */
export interface NamedPiece {
  productId: string | null
  cardKey: string | null
  label: string | null
  unavailableReason?: string | null
  unavailableMessage?: string | null
}

export type PieceReferenceState = 'card' | 'no-card' | 'unnamed'

export function pieceReferenceState(piece: NamedPiece): PieceReferenceState {
  if (piece.productId === null) return 'unnamed'
  return piece.cardKey === null ? 'no-card' : 'card'
}

/** A piece whose own id is the card key, as on every Warehouse and recovery row. */
export function pieceById(productId: string | null, label: string | null): NamedPiece {
  return { productId, cardKey: productId, label }
}

/** An order line names a piece only through `product_order_relation`. */
export function orderLinePiece(
  item: Pick<YMarketItem, 'productId' | 'productCanonicalVariantId' | 'productUnavailableReason' | 'productUnavailableMessage'>,
  label: string,
): NamedPiece {
  return {
    productId: item.productId ?? null,
    cardKey: item.productId ? item.productCanonicalVariantId ?? null : null,
    label,
    unavailableReason: item.productUnavailableReason ?? null,
    unavailableMessage: item.productUnavailableMessage ?? null,
  }
}

function Label({ value }: { value: string | null }) {
  return value === null
    ? <span className="text-ink-muted" data-unavailable="true">{ABSENT_PIECE_FACT}</span>
    : <>{value}</>
}

/**
 * The door to a piece's card from wherever the piece is named.
 *
 * Only a proven piece with an addressable card is pressable. A row that names no
 * piece prints what it carries plus the server's own sentence, once, and offers
 * nothing to press: a card opened from a resemblance is worse than no card.
 */
export function PieceReference({
  piece,
  originLabel = null,
  className = '',
  children,
}: {
  piece: NamedPiece
  originLabel?: string | null
  className?: string
  children?: ReactNode
}) {
  const location = useLocation()
  const state = pieceReferenceState(piece)

  if (state === 'unnamed') {
    const message = piece.unavailableMessage ?? null
    return (
      <span
        data-testid="piece-reference"
        data-piece-state={state}
        data-reason={piece.unavailableReason ?? undefined}
        className={`inline-flex min-w-0 flex-wrap items-baseline gap-x-2 ${className}`}
      >
        <span className="truncate text-ink"><Label value={piece.label} /></span>
        {message === null ? null : <span data-testid="piece-reference-note" className="text-[11px] text-ink-muted">{message}</span>}
      </span>
    )
  }

  if (piece.cardKey === null || piece.productId === null) {
    return (
      <span data-testid="piece-reference" data-piece-state={state} data-product-id={piece.productId ?? undefined} className={`truncate text-ink ${className}`}>
        {children ?? <Label value={piece.label} />}
      </span>
    )
  }

  const origin: PieceOrigin = { href: `${location.pathname}${location.search}`, label: originLabel }
  return (
    <Link
      to={pieceCardHref(piece.cardKey)}
      state={{ pieceOrigin: origin }}
      data-testid="piece-reference"
      data-piece-state={state}
      data-product-id={piece.productId}
      title="Открыть карточку изделия"
      className={className || 'truncate text-ink underline decoration-rule underline-offset-2 hover:text-gold-deep'}
    >
      {children ?? <Label value={piece.label} />}
    </Link>
  )
}
